import React, { useState, useEffect, useRef } from "react";
import { submitPopupMobile } from "./instrutorProfile/profileAPIs";
import { sendOtp, verifyOtp } from './instrutorProfile/profileAPIs';
import {
  X,
  Smartphone,
  Shield,
  Star,
  CheckCircle,
  Clock,
  Users,
  Award,
  ArrowRight
} from 'lucide-react';
import toast from "react-hot-toast";
import appstore from "../assets/appstore.webp";
import playstore from "../assets/playstore.webp";

interface GuestLoginPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

interface PopupMobileResponse {
  message?: string;
  dayNumber?: number;
}

const GuestLoginPopup: React.FC<GuestLoginPopupProps> = ({
  isOpen,
  onClose,
}) => {
  // OTP state
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [phoneForOtp, setPhoneForOtp] = useState('');

  // Original popup state
  const [mobile, setMobile] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const modalRef = useRef<HTMLDivElement>(null);
  const [animateIn, setAnimateIn] = useState(false);

  const resetOtpFlow = () => {
    setMobile("");
    setOtp(["", "", "", "", "", ""]);
    setOtpSent(false);
    setOtpVerified(false);
    setOtpError("");
    setResendTimer(0);
    setPhoneForOtp("");
    setError("");
  };

  // Animation on mount
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimateIn(true), 10);
    } else {
      setAnimateIn(false);
    }
  }, [isOpen]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Resend timer effect
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Store redirects
  const handleStoreRedirect = (store: 'playstore' | 'appstore') => {
    const urls = {
      playstore: "https://play.google.com/store/apps/details?id=com.metafitwellness&hl=en_IN",
      appstore: "https://apps.apple.com/us/app/metafit-wellness/id6759083882"
    };
    window.open(urls[store], "_blank");
  };

  // Send OTP function
  const handleSendOtp = async () => {
    const phone = mobile.replace(/\D/g, '');

    if (!/^\d{10}$/.test(phone)) {
      toast.error('Please enter a valid 10-digit phone number');
      return;
    }

    setIsSendingOTP(true);
    setOtpError('');

    try {
      const res = await sendOtp(phone);

      if (res.success) {
        toast.success('OTP sent successfully!');
        setOtpSent(true);
        setPhoneForOtp(phone);
        setResendTimer(60);
        setOtp(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        toast.error(res.message || 'Failed to send OTP');
      }
    } catch {
      toast.error('Failed to send OTP. Please try again.');
    } finally {
      setIsSendingOTP(false);
    }
  };

  // Verify OTP function
  const handleVerifyOtp = async () => {
    const otpValue = otp.join('');

    if (otpValue.length !== 6) {
      toast.error('Please enter complete 6-digit OTP');
      return;
    }

    setIsVerifyingOTP(true);

    try {
      const res = await verifyOtp(phoneForOtp, otpValue);

      if (res.success && res.verified) {
        toast.success('Phone number verified successfully!');
        setOtpVerified(true);
        setOtpError('');

        // After verification, submit the mobile number
        await handleSubmitAfterVerification();
      } else {
        setOtpError(res.message || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpRefs.current[0]?.focus();
      }
    } catch {
      setOtpError('Verification failed. Please try again.');
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  // Handle OTP input changes
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  // Handle OTP input keydown events
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  // Submit after OTP verification
  const handleSubmitAfterVerification = async () => {
    setIsSubmitting(true);
    setError("");

    try {
      const response = await submitPopupMobile(phoneForOtp);
      if (response?.message === "Mobile saved successfully") {
        localStorage.setItem("guest_popup_last_shown", Date.now().toString());

        toast.success('Phone verified and saved successfully!');
        setTimeout(() => {
          onClose();
          resetOtpFlow();
          setIsSuccess(false);
        }, 500);
      } else if (response?.message === "Mobile number already used") {
        resetOtpFlow();
        setError("This mobile number has already been used. Please enter another number.");
      } else {
        setError(response?.message || "Failed to submit. Please try again.");
      }
    } catch (err) {
      setError("An error occurred. Please try again later.");
      console.error("Popup submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Original submit handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const mobileRegex = /^[0-9]{10}$/;
    if (!mobile || !mobileRegex.test(mobile)) {
      setError("Please enter a valid 10-digit mobile number");
      return;
    }

    if (!otpSent) {
      await handleSendOtp();
      return;
    }

    if (!otpVerified) {
      await handleVerifyOtp();
      return;
    }

    await handleSubmitAfterVerification();
  };

  if (!isOpen) return null;
  if (isSuccess) {
    return (
      <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-md p-4">
        <div
          ref={modalRef}
          className="relative w-full max-w-md rounded-2xl bg-gradient-to-br from-[#1E3E34] via-[#24483C] to-[#15302B] p-8 shadow-2xl text-center text-white animate-in zoom-in-95 duration-300"
        >
          <div className="mb-4 animate-bounce">
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">
            Thank You!
          </h3>
          <p className="text-[#E8E1CF] mb-6">
            Your yoga journey begins now. Our team will contact you shortly.
          </p>
          <div className="text-sm text-[#ECE4CF]">
            Redirecting to homepage...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/75 backdrop-blur-md p-3 sm:p-4 md:p-6 overflow-y-auto">

      <div
        ref={modalRef}
        className={`
        relative 
        w-full 
        max-w-md md:max-w-4xl
        my-auto
        max-h-[92vh] sm:max-h-[88vh]
        bg-gradient-to-br from-[#1E3E34] via-[#24483C] to-[#15302B]
        rounded-2xl sm:rounded-3xl 
        shadow-2xl 
        overflow-hidden
        flex flex-col md:flex-row
        transition-all duration-300
        ${animateIn ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `}
      >

        {/* Close Button */}
        <button
          onClick={onClose}
          type="button"
          aria-label="Close"
          className="absolute top-3 right-3 z-30 flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60 md:bg-gray-100 md:text-gray-700 md:hover:bg-gray-200 shadow-md backdrop-blur-sm transition-all hover:scale-105 active:scale-95 cursor-pointer"
        >
          <X size={18} />
        </button>

        {/* LEFT SECTION (Branding) */}
        <div className="w-full md:w-1/2 p-4 sm:p-6 md:p-8 flex flex-col justify-between overflow-y-auto shrink-0">
          <div className="space-y-3 sm:space-y-4">
            {/* Title */}
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="bg-white p-2 sm:p-2.5 rounded-xl sm:rounded-2xl shadow-md shrink-0">
                <img
                  src="/metalogo.ico"
                  alt="Metafit Wellness"
                  className="h-9 sm:h-11 md:h-14 w-auto"
                />
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight">
                  Metafit Wellness
                </h2>
                <div className="flex items-center gap-1.5 text-xs text-[#E8E1CF] mt-0.5">
                  <Star className="w-3.5 h-3.5 text-[#E6A93F] fill-[#E6A93F]" />
                  <span>4.8 (50,000+ Downloads)</span>
                </div>
              </div>
            </div>

            <p className="text-[#E8E1CF] text-xs sm:text-sm md:text-base leading-relaxed">
              Transform your life with expert-led yoga classes, personalized wellness plans, and a supportive community.
            </p>

            {/* Features (Compact 2-col on mobile, 1-col on desktop) */}
            <div className="grid grid-cols-2 md:grid-cols-1 gap-2 pt-1 text-white text-xs sm:text-sm">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-300 shrink-0" />
                <span className="truncate">100+ Instructors</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-emerald-300 shrink-0" />
                <span className="truncate">24/7 Live Classes</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-emerald-300 shrink-0" />
                <span className="truncate">Certified Programs</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-300 shrink-0" />
                <span className="truncate">Personal Plans</span>
              </div>
            </div>
          </div>

          {/* Download Buttons */}
          <div className="pt-3 sm:pt-4 border-t border-white/10 mt-3 md:mt-4">
            <p className="text-white text-[11px] sm:text-xs font-semibold text-center mb-2">
              Download The App
            </p>
            <div className="flex flex-row gap-2.5 items-center justify-center">
              <button
                type="button"
                onClick={() => handleStoreRedirect('playstore')}
                className="transition-transform hover:scale-105 active:scale-95"
              >
                <img
                  src={playstore}
                  alt="Google Play"
                  className="w-28 sm:w-36 md:w-40 h-auto"
                />
              </button>
              <button
                type="button"
                onClick={() => handleStoreRedirect('appstore')}
                className="transition-transform hover:scale-105 active:scale-95"
              >
                <img
                  src={appstore}
                  alt="App Store"
                  className="w-28 sm:w-36 md:w-40 h-auto"
                />
              </button>
            </div>
          </div>
        </div>

        {/* RIGHT SECTION (Form) */}
        <div className="w-full md:w-1/2 bg-white p-5 sm:p-7 md:p-8 flex flex-col justify-center overflow-y-auto">
          <div className="space-y-4 sm:space-y-5 max-w-sm mx-auto w-full">
            {/* Header */}
            <div className="text-center">
              <Smartphone className="w-9 h-9 sm:w-11 sm:h-11 text-[#3F6652] mx-auto mb-1.5" />
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
                Get Started
              </h3>
              <p className="text-[#3B5750] text-xs sm:text-sm mt-0.5">
                Enter your mobile number
              </p>
            </div>

            {/* FORM */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {!otpSent && !otpVerified && (
                <div className="space-y-3">
                  <div className="relative">
                    <input
                      type="tel"
                      placeholder="Enter mobile number"
                      value={mobile}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        setMobile(digits.length <= 10 ? digits : digits.slice(-10));
                        setError("");
                      }}
                      className="w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-sm focus:border-[#3F6652] outline-none transition-colors"
                      maxLength={10}
                      autoFocus
                    />
                    <button
                      type="submit"
                      disabled={mobile.length !== 10}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#2C4A3B] text-white rounded-lg p-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#1E3E34] transition-all cursor-pointer"
                    >
                      <ArrowRight size={18} />
                    </button>
                  </div>

                  {error && (
                    <p className="text-red-500 text-xs text-center font-medium">
                      {error}
                    </p>
                  )}

                  <div className="flex justify-center items-center gap-1.5 text-gray-500 text-xs">
                    <Shield className="w-3.5 h-3.5 text-emerald-600" />
                    <span>We'll never share your number</span>
                  </div>
                </div>
              )}

              {otpSent && !otpVerified && (
                <div className="space-y-4">
                  <div className="text-center">
                    <p className="text-xs sm:text-sm text-gray-700">
                      Enter the 6-digit OTP sent to
                      <span className="font-semibold text-gray-900"> {phoneForOtp || mobile}</span>
                    </p>
                  </div>

                  <div className="flex justify-center gap-1.5 sm:gap-2">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (otpRefs.current[index] = el)}
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className="w-9 h-10 sm:w-11 sm:h-11 text-center border-2 border-gray-200 rounded-lg text-base sm:text-lg font-bold focus:ring-2 focus:ring-[#3F6652] focus:border-transparent outline-none transition-all"
                        type="text"
                        inputMode="numeric"
                      />
                    ))}
                  </div>

                  {(otpError || error) && (
                    <p className="text-center text-red-500 text-xs font-medium">
                      {otpError || error}
                    </p>
                  )}

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      disabled={isVerifyingOTP || otp.join("").length !== 6}
                      className="flex-1 bg-[#2C4A3B] text-white rounded-xl px-4 py-2.5 sm:py-3 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#1E3E34] transition-all cursor-pointer"
                    >
                      {isVerifyingOTP ? "Verifying..." : "Verify OTP"}
                    </button>

                    <button
                      type="button"
                      onClick={resetOtpFlow}
                      className="px-3 sm:px-4 py-2.5 sm:py-3 text-[#2C4A3B] text-sm font-semibold rounded-xl border-2 border-[#3F6652] hover:bg-emerald-50 transition-colors cursor-pointer"
                    >
                      Change
                    </button>
                  </div>

                  {resendTimer > 0 ? (
                    <p className="text-center text-xs text-gray-500">
                      Resend OTP in {resendTimer}s
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      disabled={isSendingOTP}
                      className="text-[#2C4A3B] hover:text-[#15302B] font-semibold text-xs mx-auto block disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {isSendingOTP ? "Sending..." : "Resend OTP"}
                    </button>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>

      </div>

    </div>
  );

};


export default GuestLoginPopup;
